/*!
 * TimbricaMidiParser — minimal Standard MIDI File (SMF) parser.
 * Supports format 0 and 1, ticks-per-quarter division (no SMPTE timecode).
 * Outputs a `{ format, ticksPerQuarter, bpm, notes:[{midi,startMs,endMs,velocity,channel}] }`.
 *
 * Events handled:
 *   - Note on (0x9n) with velocity > 0
 *   - Note off (0x8n or note-on with vel=0)
 *   - CC#64 sustain pedal (extends held notes through pedal-up)
 *   - Tempo meta (0x51) — adjusts tempo map
 *   - Time signature meta (0x58) — preserved on output (not used by lane)
 *   - End of track (0x2F)
 *
 * Skipped (read length, advance offset):
 *   - All other meta events
 *   - Polyphonic pressure (0xAn), Program change (0xCn), Channel pressure (0xDn),
 *     Pitch bend (0xEn), CC other than #64
 *   - SysEx (0xF0 / 0xF7)
 *
 * Rejects: format 2, SMPTE division (negative bytes), files > 2 MB (handled by caller).
 *
 * ES5-safe, no dependencies.
 */
window.TimbricaMidiParser = (function () {
    'use strict';

    function readUint32(buf, off) {
        return (buf[off] << 24 >>> 0) + (buf[off + 1] << 16) + (buf[off + 2] << 8) + buf[off + 3];
    }
    function readUint16(buf, off) {
        return (buf[off] << 8) + buf[off + 1];
    }
    function readString(buf, off, len) {
        var s = '';
        for (var i = 0; i < len; i++) s += String.fromCharCode(buf[off + i]);
        return s;
    }

    /* Variable-length quantity */
    function readVLQ(buf, off) {
        var value = 0;
        var bytes = 0;
        while (bytes < 4) {
            var b = buf[off + bytes];
            value = (value << 7) | (b & 0x7F);
            bytes++;
            if ((b & 0x80) === 0) break;
        }
        return { value: value, bytes: bytes };
    }

    function parse(buf) {
        if (buf.length < 14) throw new Error('Too short to be a MIDI file');
        if (readString(buf, 0, 4) !== 'MThd') throw new Error('Missing MThd header');
        if (readUint32(buf, 4) !== 6) throw new Error('Unexpected MThd length');
        var format = readUint16(buf, 8);
        if (format > 1) throw new Error('Only format 0/1 supported');
        var numTracks = readUint16(buf, 10);
        var division = readUint16(buf, 12);
        if (division & 0x8000) throw new Error('SMPTE division not supported');
        /* Zero ticks per quarter times nothing: every note time is x/0. Such a file
           cannot be played as written, and the old reading handed NaN note times to
           the synth (Sentry SK, /online-piano: setValueAtTime «non-finite»). */
        if (!division) throw new Error('Invalid MIDI division: 0 ticks per quarter note');
        var ticksPerQuarter = division;

        /* Which notes the file plays is decided in ONE place for the whole site
           (midi-stream-notes.js). The pairing below kept one sounding note per
           key and OVERWROTE it on a second strike, so the first strike vanished:
           measured on the Lakh corpus (2026-09-25), 1 011 762 notes lost in
           9 761 of 17 190 files. It also let a meta event take over running
           status, which desynchronises every channel event after it. */
        var stream = window.MidiStreamNotes ? window.MidiStreamNotes.read(buf) : null;
        if (stream) return fromStream(stream, format, ticksPerQuarter);

        var off = 14;
        var trackEvents = [];   /* flat array across all tracks, with absolute tick time */

        for (var t = 0; t < numTracks; t++) {
            if (off + 8 > buf.length) break;
            if (readString(buf, off, 4) !== 'MTrk') {
                /* Skip unknown chunk */
                var skipLen = readUint32(buf, off + 4);
                off += 8 + skipLen;
                continue;
            }
            var trackLen = readUint32(buf, off + 4);
            var trackEnd = off + 8 + trackLen;
            off += 8;

            var absTick = 0;
            var runningStatus = 0;

            while (off < trackEnd) {
                var vlq = readVLQ(buf, off);
                off += vlq.bytes;
                absTick += vlq.value;

                var status = buf[off];
                if (status < 0x80) {
                    /* Running status — reuse last */
                    status = runningStatus;
                } else {
                    off++;
                    runningStatus = status;
                }

                var hi = status & 0xF0;
                var ch = status & 0x0F;

                if (hi === 0x80 || hi === 0x90) {
                    var note = buf[off++];
                    var vel = buf[off++];
                    var type = (hi === 0x90 && vel > 0) ? 'on' : 'off';
                    trackEvents.push({ tick: absTick, type: type, midi: note, velocity: vel, channel: ch });
                } else if (hi === 0xA0) {
                    off += 2; /* poly pressure — skip */
                } else if (hi === 0xB0) {
                    var cc = buf[off++];
                    var ccVal = buf[off++];
                    if (cc === 64) {
                        trackEvents.push({ tick: absTick, type: 'sustain', value: ccVal, channel: ch });
                    }
                    /* other CCs ignored */
                } else if (hi === 0xC0) {
                    off += 1; /* program change */
                } else if (hi === 0xD0) {
                    off += 1; /* channel pressure */
                } else if (hi === 0xE0) {
                    off += 2; /* pitch bend */
                } else if (status === 0xFF) {
                    /* Meta */
                    var metaType = buf[off++];
                    var lenVlq = readVLQ(buf, off);
                    off += lenVlq.bytes;
                    var dataStart = off;
                    var dataLen = lenVlq.value;
                    if (metaType === 0x51 && dataLen === 3) {
                        var usPerQuarter = (buf[dataStart] << 16) + (buf[dataStart + 1] << 8) + buf[dataStart + 2];
                        trackEvents.push({ tick: absTick, type: 'tempo', usPerQuarter: usPerQuarter });
                    } else if (metaType === 0x2F) {
                        /* End of track */
                        off = trackEnd;
                        break;
                    }
                    off = dataStart + dataLen;
                } else if (status === 0xF0 || status === 0xF7) {
                    /* SysEx — skip via length VLQ */
                    var sLen = readVLQ(buf, off);
                    off += sLen.bytes + sLen.value;
                } else {
                    /* Unknown — bail to avoid infinite loop */
                    break;
                }
            }
            off = trackEnd;
        }

        /* Sort by tick */
        trackEvents.sort(function (a, b) { return a.tick - b.tick; });

        /* Build tempo map and convert ticks → ms */
        var defaultUs = 500000; /* 120 BPM */
        var tempoMap = [{ tick: 0, usPerQuarter: defaultUs }];
        for (var i = 0; i < trackEvents.length; i++) {
            if (trackEvents[i].type === 'tempo' && trackEvents[i].usPerQuarter > 0) {   /* a zero tempo is no tempo (see fromStream) */
                tempoMap.push({ tick: trackEvents[i].tick, usPerQuarter: trackEvents[i].usPerQuarter });
            }
        }
        function tickToMs(tick) {
            var ms = 0;
            var lastTick = 0;
            var lastUs = defaultUs;
            for (var j = 0; j < tempoMap.length; j++) {
                if (tempoMap[j].tick >= tick) break;
                ms += (tempoMap[j].tick - lastTick) * (lastUs / 1000) / ticksPerQuarter;
                lastTick = tempoMap[j].tick;
                lastUs = tempoMap[j].usPerQuarter;
            }
            ms += (tick - lastTick) * (lastUs / 1000) / ticksPerQuarter;
            return ms;
        }

        /* Pair note-on/off into notes */
        var notes = [];
        var open = {}; /* "ch:midi" → noteOn record */
        for (var k = 0; k < trackEvents.length; k++) {
            var e = trackEvents[k];
            if (e.type === 'on') {
                var key = e.channel + ':' + e.midi;
                open[key] = { startTick: e.tick, midi: e.midi, velocity: e.velocity / 127, channel: e.channel };
            } else if (e.type === 'off') {
                var k2 = e.channel + ':' + e.midi;
                if (open[k2]) {
                    notes.push({
                        midi: e.midi,
                        startMs: tickToMs(open[k2].startTick),
                        endMs: tickToMs(e.tick),
                        velocity: open[k2].velocity,
                        channel: e.channel
                    });
                    delete open[k2];
                }
            }
        }

        /* BPM (from first tempo) */
        var firstUs = tempoMap.length > 1 ? tempoMap[1].usPerQuarter : defaultUs;
        var bpm = Math.round(60000000 / firstUs);

        return {
            format: format,
            ticksPerQuarter: ticksPerQuarter,
            bpm: bpm,
            notes: notes
        };
    }

    /** Same output shape as the legacy path below, built from the door's reading. */
    function fromStream(stream, format, ticksPerQuarter) {
        var defaultUs = 500000; /* 120 BPM */
        var marks = [{ tick: 0, us: defaultUs, ms: 0 }];
        /* A tempo of 0 µs per quarter is not a tempo (the field has no «stop»): taken
           as written it made the first-tempo BPM 60e6/0 = Infinity, and the piano's
           re-quantisation (60000 / bpm per tick) turned every note time into NaN —
           Sentry SK. Such an event is skipped: the tempo before it stays. */
        var tempos = stream.tempos.filter(function (t) { return t.usPerQuarter > 0; });
        tempos.forEach(function (t) {
            var last = marks[marks.length - 1];
            var tick = Math.max(last.tick, t.ticks);
            marks.push({ tick: tick, us: t.usPerQuarter, ms: last.ms + (tick - last.tick) * (last.us / 1000) / ticksPerQuarter });
        });
        function tickToMs(tick) {
            var m = marks[0];
            for (var j = 1; j < marks.length && marks[j].tick <= tick; j++) m = marks[j];
            return m.ms + (tick - m.tick) * (m.us / 1000) / ticksPerQuarter;
        }
        var notes = [];
        stream.tracks.forEach(function (t) {
            t.notes.forEach(function (n) {
                notes.push({
                    midi: n.midi,
                    startMs: tickToMs(n.ticks),
                    endMs: tickToMs(n.ticks + n.durationTicks),
                    velocity: n.velocity / 127,
                    channel: n.channel
                });
            });
        });
        notes.sort(function (a, b) { return a.startMs - b.startMs; });
        var firstUs = tempos.length ? tempos[0].usPerQuarter : defaultUs;
        return {
            format: format,
            ticksPerQuarter: ticksPerQuarter,
            bpm: Math.round(60000000 / firstUs),
            notes: notes
        };
    }

    return { parse: parse };
})();
